import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, Renderer2, ViewChild } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Action } from '@ngrx/store';
import { UUID } from 'angular2-uuid';
import { Content, InfiniteScroll, Platform, TextInput } from 'ionic-angular';
import { KEY_DOWN, KEY_ENTER, KEY_ESCAPE, KEY_TAB, KEY_UP } from 'ionic-angular/platform/key';
import { Observable } from 'rxjs';
import { VasAppFacade } from '../../../services/vas-app-facade.service';
import { ControlComponent } from '../../control.component';
import { TextControl } from '../text-control.model';
import { SearchResultModel } from './search-result.model';
import * as actions from './text-control.actions';
import * as selectors from './text-control.selectors';

@Component({
    selector: 'text-control',
    templateUrl: 'text-control.component.html',
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => TextControlComponent), multi: true}
    ]
})
export class TextControlComponent extends ControlComponent {
    @Input() definition: TextControl;
    @Output() changes: EventEmitter<any> = new EventEmitter();

    @ViewChild('input') inputElem: TextInput;
    @ViewChild('results') ionContentContainer: ElementRef;
    @ViewChild(Content) ionContent: Content;
    @ViewChild(InfiniteScroll) ionInfiniteScroll: InfiniteScroll;

    instanceKey = UUID.UUID();
    resultsElemMoved = false;
    _rootElem: Element;
    disableBlur = false;
    readonly containerHeight = 250;

    canLoadMore$: Observable<boolean> =
        this.appFacade.store.select(
            selectors.getCanLoadMore(this.instanceKey)
        );

    inputValue$: Observable<string> =
        this.appFacade.store.select(
            selectors.getInputValue(this.instanceKey)
        );

    searchResults$: Observable<Array<SearchResultModel>> =
        this.appFacade.store.select(
            selectors.getResults(this.instanceKey)
        );

    showResults$: Observable<boolean> =
        this.appFacade.store.select(
            selectors.getShowResults(this.instanceKey)
        );

    searchEnabled$: Observable<boolean> =
        this.appFacade.store.select(
            selectors.getSearchEnabled(this.instanceKey)
        );

    showResultsWindow$: Observable<boolean> =
        this.appFacade.store.select(
            selectors.getShowResultsWindow(this.instanceKey)
        );

    constructor(
        protected appFacade: VasAppFacade,
        protected elemRef: ElementRef,
        protected renderer: Renderer2,
        protected platformService: Platform
    ) {
        super(elemRef);
    }

    get inputNativeElem(): HTMLInputElement {
        return this.inputElem && this.inputElem.getNativeElement();
    }

    get isMobile(): boolean {
        return !this.platformService.is('core');
    }

    get rootElem(): Element {
        if (this._rootElem) return this._rootElem;
        return this._rootElem = document
            .getElementsByTagName('ion-app')
            .item(0);
    }

    get ionContentContainerNativeElem(): Element {
        return this.ionContentContainer &&
            this.ionContentContainer.nativeElement;
    }

    get ionContentNativeElem(): Element {
        return this.ionContent &&
            this.ionContent.getNativeElement();
    }

    ngOnInit(): void {

        super.ngOnInit();

        this.dispatch(new actions.InitAction({
            instanceKey: this.instanceKey,
            control: this.definition,
            inputValue: this.value
        }));

        this.inputValue$
            .debounceTime(300)
            .takeUntil(this.ngUnsubscribe)
            .subscribe(inputValue => {

                if (!this._value && !inputValue) {
                    return;
                }

                this.updateValue(inputValue);
                // this.inputElem.setValue(inputValue);
            });

        this.searchResults$
            .filter(results => results.length > 0)
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => {
                this.positionResults();
            });
    }

    ngAfterViewInit(): void {
        this.moveResultsElementToRoot();

        const elem: Element = this.ionContentNativeElem;

        if (elem) {
            elem.querySelector('.scroll-content').addEventListener(
                'scroll',
                ev => {
                    (this.ionInfiniteScroll as any)._content =
                        this.ionContent;
                    this.ionInfiniteScroll._onScroll(ev as any);
                });

            (this.ionContent as any)._readDimensions();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        try {
            this.renderer.removeChild(
                this.rootElem,
                this.ionContentContainer.nativeElement
            );
        } catch (e) {
            console.warn(e);
        }

        this.dispatch(new actions.DestroyAction({
            instanceKey: this.instanceKey
        }));
    }

    onBlur(): void {

        if (this.disableBlur) {
            return;
        }

        this.dispatch(new actions.BlurAction({
            instanceKey: this.instanceKey
        }));
    }

    onFocus(): void {
        this.inputElem.setFocus();

        if (this.isMobile) {
            return;
        }

        this.dispatch(new actions.ShowAllAction({
            instanceKey: this.instanceKey,
            lookupTypeId: this.definition.config.lookupType
        }));
    }

    onClickNewLookup($event: Event): void {
        this.cancelEvent($event);

        this.dispatch(new actions.NewLookupAction({
            accountId: this.definition.account,
            instanceKey: this.instanceKey,
            lookupTypeId: this.definition.config.lookupType,
            accessGroupId: this.definition.config.accessGroup
        }));
    }

    onClickResult($event, result: SearchResultModel): void {
        this.cancelEvent($event);

        this.dispatch(new actions.ClickResultAction({
            instanceKey: this.instanceKey,
            result
        }));
    }

    onClickSearch(): void {
        this.dispatch(new actions.ShowAllAction({
            instanceKey: this.instanceKey,
            lookupTypeId: this.definition.config.lookupType
        }));
    }

    onKeyDown($event: KeyboardEvent): void {

        // do not allow KEY_ENTER events to propagate
        if ($event.keyCode === KEY_ENTER) {
            this.cancelEvent($event);
        }
    }

    onKeyUp($event: KeyboardEvent): void {
        switch ($event.keyCode) {
            case KEY_DOWN: {
                this.dispatch(new actions.KeyDownAction({
                    instanceKey: this.instanceKey
                }));
                setTimeout(
                    () => this.scrollSelectedElemInToView(),
                    100
                );
                return;
            }
            case KEY_ENTER: {
                this.dispatch(new actions.KeyEnterAction({
                    instanceKey: this.instanceKey
                }));
                return;
            }
            case KEY_ESCAPE: {
                this.dispatch(new actions.KeyEscAction({
                    instanceKey: this.instanceKey
                }));
                return;
            }
            // left shift
            case 16:
            case KEY_TAB: {
                // ignore the key tab
                return;
            }
            case KEY_UP: {
                this.dispatch(new actions.KeyUpAction({
                    instanceKey: this.instanceKey
                }));
                setTimeout(
                    () => this.scrollSelectedElemInToView(),
                    100
                );
                return;
            }
            default: {
                this.dispatch(new actions.SetSearchTermAction({
                    instanceKey: this.instanceKey,
                    searchTerm: this.inputElem.value
                }));
            }
        }
    }

    onMouseEnterResults(): void {
        this.disableBlur = true;
    }

    onMouseLeaveResults(): void {
        this.disableBlur = false;
    }

    onLoadMore(): void {
        this.dispatch(new actions.LoadMoreAction({
            instanceKey: this.instanceKey
        }));
        setTimeout(
            () => this.ionInfiniteScroll.complete(),
            10
        );
    }

    updateValue(value: string): void {
        super.updateValue(value);
        this.inputElem.value = value;
    }

    protected scrollSelectedElemInToView(): void {
        try {
            const scrollContent = this.ionContentNativeElem.querySelector(
                '.scroll-content'
            );
            const elem = this.ionContentContainerNativeElem.querySelector(
                '.selected'
            ) as HTMLElement;
            scrollContent.scrollTop = elem ? elem.offsetTop : 0;
        } catch (e) {
            console.warn(e);
        }
    }

    protected moveResultsElementToRoot(): void {
        const resultsElem: ElementRef = this.ionContentContainer;

        if (
            this.resultsElemMoved ||
            !this.inputNativeElem ||
            !resultsElem ||
            !this.rootElem
        ) {
            return;
        }

        const p = resultsElem.nativeElement.parentNode;

        if (!p) {
            return;
        }

        if (p !== this.rootElem) {
            this.renderer.removeChild(p, resultsElem);
            this.renderer.appendChild(this.rootElem, resultsElem.nativeElement);
        }

        this.resultsElemMoved = true;
    }

    protected positionResults(): void {
        try {
            const containerElem = this.ionContentContainerNativeElem;
            const bb = this.inputNativeElem.querySelector('input')
                .getBoundingClientRect();
            let top = bb.top + this.inputNativeElem.offsetHeight;
            const containerHeight = this.containerHeight;

            this.renderer.setStyle(containerElem, 'left', `${bb.left}px`);

            // if the container will overflow the viewport height
            if (top + containerHeight > window.innerHeight) {
                // set the bottom of the container to the top of the input
                top = bb.top - containerHeight;
            }
            this.renderer.setStyle(
                containerElem,
                'top',
                `${top}px`
            );

        } catch (e) {
            console.warn(e);
        }
    }

    protected dispatch(action: Action): void {
        this.appFacade.store.dispatch(action);
    }

    protected cancelEvent($event: Event): void {
        try {
            $event.preventDefault();
            $event.stopPropagation();
        } catch (e) {
            // noop
        }
    }

    trackBy(index: number, item: SearchResultModel): string {
        return `${item.id}-${item.highlighted}`;
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/text/form/text-control.component.ts